/*
Module-level "active route" state.

A walking route is computed on the species detail screen and consumed
by the map screen — they're parallel mounts under the router (one
unmounts before the other mounts), so we can't pass the plan as a
function arg. A small pub/sub here is enough: SpeciesScreen calls
`setActiveRoute(plan)` then navigates to the map, MapScreen
subscribes during mount, paints, and tears down on unmount.

The state lives only in memory. A page reload clears it — that's
fine: routes are session-scoped, the user doesn't expect them to
persist after closing the tab.
*/

import type { RoutePlan } from './routePlanner'

let fCurrent: RoutePlan | null = null
const fListeners = new Set<(inPlan: RoutePlan | null) => void>()

/*
Replace the active route. Pass null to clear (e.g. when the user
exits the route from the map screen banner). Notifies subscribers
synchronously.
*/
export function setActiveRoute(inPlan: RoutePlan | null): void
{
	fCurrent = inPlan
	for (const vFn of fListeners) vFn(inPlan)
}

/*
Read the current plan without subscribing. Useful for "did the user
arrive on the map screen with a plan already set?" cold-start checks.
*/
export function getActiveRoute(): RoutePlan | null { return fCurrent }

/*
Subscribe. Fires once immediately with the current value, then on
every setActiveRoute call. Returns the unsubscribe closure.
*/
export function subscribeRoute(inFn: (inPlan: RoutePlan | null) => void): () => void
{
	fListeners.add(inFn)
	inFn(fCurrent)
	return () => { fListeners.delete(inFn) }
}
